import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { useAuth } from "./AuthContext.js";
import { logViewPost } from "./user_log.js";

/**
 * Observe an element and fire POST /user_log/log-view-post once when it
 * becomes sufficiently visible in the viewport.
 *
 * @param {string|null|undefined} postId
 * @param {{ enabled?: boolean, threshold?: number }} [options]
 * @returns {React.RefObject}
 */
export function usePostViewTracker(postId, { enabled = true, threshold = 0.35 } = {}) {
  const ref = useRef(null);
  const loggedRef = useRef(false);
  const { getIdToken } = useAuth();

  useEffect(() => {
    loggedRef.current = false;
  }, [postId]);

  useEffect(() => {
    if (!enabled || !postId || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const el = ref.current;
    if (!el) return undefined;

    let cancelled = false;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || loggedRef.current || cancelled) return;

        loggedRef.current = true;
        observer.disconnect();

        (async () => {
          try {
            let token = null;
            try {
              token = await getIdToken();
            } catch {
              token = null;
            }
            const cookieId = Cookies.get("uid") || undefined;
            await logViewPost({ postId, token, cookieId });
          } catch (err) {
            console.error("Error logging post view:", err);
          }
        })();
      },
      { threshold, rootMargin: "0px" }
    );

    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [postId, enabled, threshold, getIdToken]);

  return ref;
}
